// Public, browser-safe credentials. The anon key is designed to be exposed in
// the frontend; security is enforced by Row Level Security on each table
// (these Phase-1 tables only allow anonymous INSERT, no read).
//
// Set these in .env.local for local dev, and in the Netlify dashboard for prod:
//   REACT_APP_SUPABASE_URL
//   REACT_APP_SUPABASE_ANON_KEY
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

if (!isSupabaseConfigured) {
  // Missing env vars must not crash the app at import time — Navbar loads
  // Modal eagerly, which imports this module before React can render.
  console.error(
    'Supabase env vars missing. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env.local'
  );
}

// The Supabase client is ~340KB of JS and is only needed when a form is submitted,
// so it is loaded on demand (and prefetched at idle priority) instead of being part of
// the main bundle. Callers keep using `supabase.from(table).insert(...)` unchanged.
let clientPromise = null;
const loadClient = () => {
  if (!clientPromise) {
    clientPromise = import(/* webpackPrefetch: true */ '@supabase/supabase-js').then(
      ({ createClient }) => createClient(supabaseUrl, supabaseAnonKey)
    );
  }
  return clientPromise;
};

export const supabase = isSupabaseConfigured
  ? {
      from: (table) => ({
        insert: async (...args) => {
          try {
            const client = await loadClient();
            const result = await client.from(table).insert(...args);

            // Attribution must never cost us a lead: if the insert failed
            // because a utm_* column is missing on the table, retry once
            // without the UTM fields.
            const row = args[0];
            if (
              result.error &&
              row && !Array.isArray(row) && typeof row === 'object' &&
              /utm_/i.test(`${result.error.message || ''} ${result.error.details || ''}`)
            ) {
              const stripped = Object.fromEntries(
                Object.entries(row).filter(([key]) => !key.startsWith('utm_'))
              );
              return await client.from(table).insert(stripped, ...args.slice(1));
            }
            return result;
          } catch (error) {
            clientPromise = null; // allow a retry if the chunk failed to load
            return { data: null, error };
          }
        },
      }),
    }
  : null;

export default supabase;
